import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Clock, MapPin, AlertTriangle, User, Phone, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

// Utility: day & week ranges
const getDayRange = (dateStr) => {
  const base = dateStr ? new Date(dateStr + "T00:00:00") : new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0);
  const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
};

const getWeekRange = (dateStr) => {
  const base = dateStr ? new Date(dateStr + "T00:00:00") : new Date();
  const day = base.getDay(); // 0=Sun
  const mondayOffset = (day === 0 ? -6 : 1) - day;
  const monday = new Date(base);
  monday.setDate(base.getDate() + mondayOffset);
  const start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString(), monday };
};

const ACTIVE_STATUSES = ["scheduled", "confirmed", "in_route", "arrived"];

const statusColors = {
  scheduled: "bg-slate-700 text-slate-100",
  confirmed: "bg-emerald-500 text-slate-900",
  in_route: "bg-amber-500 text-slate-900",
  arrived: "bg-emerald-600 text-white",
  completed: "bg-emerald-700 text-white",
  no_show: "bg-red-700 text-white",
  canceled: "bg-slate-600 text-slate-100",
  rescheduled: "bg-slate-500 text-slate-100",
};

const roleIsOffice = (role) => ["owner", "office_manager", "project_manager", "admin"].includes(role || "");

const buildGoogleMapsLink = (address) => {
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const isAtRisk = (appt, inRouteMinutes) => {
  if (!appt?.scheduled_start) return false;
  if (!["scheduled", "confirmed"].includes(appt.status)) return false;
  const thresholdMinutes = inRouteMinutes ?? 60;
  const start = new Date(appt.scheduled_start).getTime();
  const now = Date.now();
  const thresholdMs = thresholdMinutes * 60 * 1000;
  return now >= start - thresholdMs;
};

const formatTime = (iso) => {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const StatusBadge = ({ status }) => {
  const cls = statusColors[status] || "bg-slate-700 text-slate-100";
  return <Badge className={cls}>{status.replace("_", " ")}</Badge>;
};

const AtRiskBadge = ({ appt, inRouteMinutes, isEstimatorView }) => {
  if (!isAtRisk(appt, inRouteMinutes)) return null;
  const time = formatTime(appt.scheduled_start);
  return (
    <Badge className="bg-amber-500 text-slate-900 text-xs flex items-center gap-1">
      <AlertTriangle className="h-3 w-3" />
      AT RISK
      {isEstimatorView && (
        <span className="ml-1">In Route required by {time}</span>
      )}
    </Badge>
  );
};

const AppointmentCard = ({
  appt,
  isOffice,
  isEstimator,
  currentUserId,
  inRouteMinutes,
  onStatusChange,
  onOpenLead,
  onOpenNotes,
}) => {
  const mapsUrl = buildGoogleMapsLink(appt.location_address);
  const startTime = formatTime(appt.scheduled_start);

  return (
    <Card className="bg-slate-900 border-slate-700">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-slate-400" />
            <p className="text-sm text-slate-100 font-medium">{startTime}</p>
          </div>
          <div className="flex items-center gap-2">
            <AtRiskBadge
              appt={appt}
              inRouteMinutes={inRouteMinutes}
              isEstimatorView={isEstimator}
            />
            <StatusBadge status={appt.status} />
          </div>
        </div>

        <div className="space-y-1">
          <button
            type="button"
            onClick={() => onOpenLead?.(appt.lead_id)}
            className="flex items-center gap-2 text-sm text-slate-100 hover:text-emerald-300"
          >
            <User className="h-4 w-4 text-slate-400" />
            <span className="font-medium truncate">
              {appt.lead_contact_name || "Lead"}
            </span>
            <ChevronRight className="h-3 w-3" />
          </button>
          {appt.location_address && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{appt.location_address}</span>
            </div>
          )}
          {appt.assigned_estimator_name && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Clock className="h-3 w-3" />
              <span>Estimator: {appt.assigned_estimator_name}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-800 mt-2">
          <div className="flex items-center gap-2">
            {mapsUrl && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs border-slate-600 text-slate-200 hover:bg-slate-800"
                asChild
              >
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                  <MapPin className="h-3 w-3 mr-1" />
                  Directions
                </a>
              </Button>
            )}
            {appt.lead_phone && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs border-slate-600 text-slate-200 hover:bg-slate-800"
                asChild
              >
                <a href={`tel:${appt.lead_phone}`}>
                  <Phone className="h-3 w-3 mr-1" />
                  Call
                </a>
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2 text-xs border-slate-600 text-slate-200 hover:bg-slate-800"
              onClick={() => onOpenNotes?.(appt)}
            >
              Notes
            </Button>
          </div>
          <div className="flex items-center gap-1">
            {isEstimator &&
              onStatusChange &&
              appt.assigned_estimator_user_id === currentUserId && (
                <>
                  {(appt.status === "scheduled" || appt.status === "confirmed") && (
                    <Button
                      size="sm"
                      className="h-8 px-2 bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs"
                      onClick={() => onStatusChange(appt, "in_route")}
                    >
                      In Route
                    </Button>
                  )}
                  {appt.status === "in_route" && (
                    <Button
                      size="sm"
                      className="h-8 px-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                      onClick={() => onStatusChange(appt, "arrived")}
                    >
                      Arrived
                    </Button>
                  )}
                  {appt.status === "arrived" && (
                    <Button
                      size="sm"
                      className="h-8 px-2 bg-emerald-700 hover:bg-emerald-600 text-white text-xs"
                      onClick={() => onStatusChange(appt, "completed")}
                    >
                      Complete
                    </Button>
                  )}
                  {["scheduled", "confirmed", "in_route", "arrived"].includes(
                    appt.status
                  ) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2 text-xs border-red-500 text-red-300 hover:bg-red-950"
                      onClick={() => onStatusChange(appt, "no_show")}
                    >
                      No Show
                    </Button>
                  )}
                </>
              )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
// Helper: local datetime utils for quick-create and modal
const parseLocalDateTime = (localStr) => {
  if (!localStr) return null;
  // Treat as local time; browser will interpret without timezone offset in ISO string
  return new Date(localStr);
};

const formatLocalDateTime = (date) => {
  if (!date) return "";
  // Returns YYYY-MM-DDTHH:mm for datetime-local inputs
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const setLocalDatePart = (localStr, newDate) => {
  const base = parseLocalDateTime(localStr) || newDate || new Date();
  const d = newDate || base;
  const withNew = new Date(base);
  withNew.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
  return formatLocalDateTime(withNew);
};

const setLocalTimePart = (localStr, hours, minutes) => {
  const base = parseLocalDateTime(localStr) || new Date();
  const withNew = new Date(base);
  withNew.setHours(hours, minutes, 0, 0);
  return formatLocalDateTime(withNew);
};

// Time options for 15-minute increments
const TIME_OPTIONS = Array.from({ length: 96 }, (_, idx) => {
  const minutes = idx * 15;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const label = new Date(0, 0, 0, h, m).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return { value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, label };
});

// Google Calendar-style quick create card
const GCalQuickCreateCard = ({
  open,
  anchor,
  value,
  onChange,
  onSave,
  onMoreOptions,
  onClose,
  isLeadLocked,
  eventType,
  onEventTypeChange,
}) => {
  const [leadQuery, setLeadQuery] = useState("");
  const [leadResults, setLeadResults] = useState([]);

  useEffect(() => {
    if (!open || isLeadLocked || eventType !== "appointment") {
      return;
    }
    if (!leadQuery || leadQuery.length < 2) {
      if (leadResults.length) {
        setLeadResults([]);
      }
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const res = await API.get("/leads/search", {
          params: { q: leadQuery, limit: 10 },
        });
        if (!cancelled) {
          setLeadResults(Array.isArray(res.data) ? res.data : []);
        }
      } catch (_e) {
        if (!cancelled) setLeadResults([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, isLeadLocked, eventType, leadQuery]);

  if (!open) return null;

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 768;
  const cardWidth = Math.min(720, viewportWidth - 24);
  const margin = 8;

  let left = (anchor?.x || viewportWidth / 2) - cardWidth / 2;
  if (left < margin) left = margin;
  if (left + cardWidth > viewportWidth - margin) left = viewportWidth - margin - cardWidth;

  const estimatedHeight = 260;
  let top = (anchor?.y || viewportHeight / 2) - estimatedHeight / 2;
  if (top < margin) top = margin;
  if (top + estimatedHeight > viewportHeight - margin) top = viewportHeight - margin - estimatedHeight;

  // lead search hook removed duplicate

  const handleDateChange = (newDate) => {
    if (!newDate) return;
    const next = {
      ...value,
      startLocal: setLocalDatePart(value.startLocal, newDate),
      endLocal: setLocalDatePart(value.endLocal || value.startLocal, newDate),
    };
    onChange?.(next);
  };

  const handleTimeChange = (field, timeValue) => {
    const [h, m] = timeValue.split(":").map((x) => parseInt(x, 10));
    const next = { ...value };
    if (field === "start") {
      next.startLocal = setLocalTimePart(value.startLocal, h, m);
      // auto-bump end if missing or before start
      if (!value.endLocal) {
        const startDate = parseLocalDateTime(next.startLocal);
        const end = new Date(startDate.getTime() + 60 * 60000);
        next.endLocal = formatLocalDateTime(end);
      } else {
        const startDate = parseLocalDateTime(next.startLocal);
        const endDate = parseLocalDateTime(value.endLocal);
        if (endDate && endDate <= startDate) {
          const bumped = new Date(startDate.getTime() + 60 * 60000);
          next.endLocal = formatLocalDateTime(bumped);
        }
      }
    } else {
      next.endLocal = setLocalTimePart(value.endLocal || value.startLocal, h, m);
      const startDate = parseLocalDateTime(next.startLocal);
      const endDate = parseLocalDateTime(next.endLocal);
      if (startDate && endDate && endDate <= startDate) {
        const bumped = new Date(startDate.getTime() + 60 * 60000);
        next.endLocal = formatLocalDateTime(bumped);
      }
    }
    onChange?.(next);
  };

  const startDate = parseLocalDateTime(value.startLocal) || new Date();

  const currentStartTime = value.startLocal?.slice(11, 16) || "09:00";
  const currentEndTime = value.endLocal?.slice(11, 16) || "10:00";

  return (
    <div
      className="fixed inset-0 z-40"
      onClick={() => onClose?.()}
    >
      <div
        className="absolute z-50 calendar-quick-card bg-slate-950 border border-slate-700 shadow-2xl rounded-[12px] text-slate-100"
        style={{
          width: cardWidth,
          top,
          left,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 calendar-quick-card-header" style={{ height: 44 }}>
          <div className="flex items-center gap-2 text-slate-400">
            <span className="w-1.5 h-5 rounded-full bg-slate-600" />
            <span className="text-xs uppercase tracking-[0.12em] text-slate-400">
              Create event
            </span>
          </div>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-100 text-sm"
            onClick={() => onClose?.()}
          >
            ✕
          </button>
        </div>
        <div className="px-5 pb-4 pt-1" style={{ paddingTop: 4 }}>
          {/* Type tabs */}
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => onEventTypeChange?.("event")}
              className={`px-3 py-1 rounded-full text-xs border ${
                eventType === "event"
                  ? "bg-emerald-500 text-slate-950 border-emerald-400"
                  : "bg-slate-900 text-slate-300 border-slate-700"
              }`}
            >
              Event
            </button>
            <button
              type="button"
              onClick={() => onEventTypeChange?.("appointment")}
              className={`px-3 py-1 rounded-full text-xs border ${
                eventType === "appointment"
                  ? "bg-emerald-500 text-slate-950 border-emerald-400"
                  : "bg-slate-900 text-slate-300 border-slate-700"
              }`}
            >
              Appointment (Lead/Client)
            </button>
          </div>

          {/* Title (UI only; we bind to notes_internal for now) */}
          <div className="mb-2">
            <input
              type="text"
              placeholder="Add title"
              value={value.title || ""}
              onChange={(e) => onChange?.({ ...value, title: e.target.value })}
              className="w-full bg-transparent text-[20px] leading-[44px] h-[44px] border-b border-slate-700 focus:outline-none focus:border-emerald-500 placeholder:text-slate-500 calendar-quick-card-title"
            />
          </div>

          {/* Date & time row */}
          <div className="flex items-center gap-2 mb-3 text-[13px]">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="px-2 py-1 rounded-md hover:bg-slate-800 border border-slate-700 text-slate-100 text-xs flex items-center gap-1"
                >
                  {startDate.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    weekday: "short",
                  })}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0 bg-slate-950 border-slate-700">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={handleDateChange}
                  className="bg-slate-950 text-slate-100"
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="px-2 py-1 rounded-md hover:bg-slate-800 border border-slate-700 text-slate-100 text-xs min-w-[68px] text-left"
                >
                  {TIME_OPTIONS.find((t) => t.value === currentStartTime)?.label || "9:00 AM"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[120px] max-h-[260px] overflow-y-auto bg-slate-950 border-slate-700 p-0">
                <ul className="text-xs">
                  {TIME_OPTIONS.map((t) => (
                    <li key={t.value}>
                      <button
                        type="button"
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 ${
                          t.value === currentStartTime ? "bg-slate-800 text-emerald-400" : ""
                        }`}
                        onClick={() => handleTimeChange("start", t.value)}
                      >
                        {t.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>

            <span className="text-slate-500 text-xs">to</span>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="px-2 py-1 rounded-md hover:bg-slate-800 border border-slate-700 text-slate-100 text-xs min-w-[68px] text-left"
                >
                  {TIME_OPTIONS.find((t) => t.value === currentEndTime)?.label || "10:00 AM"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[120px] max-h-[260px] overflow-y-auto bg-slate-950 border-slate-700 p-0">
                <ul className="text-xs">
                  {TIME_OPTIONS.map((t) => (
                    <li key={t.value}>
                      <button
                        type="button"
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 ${
                          t.value === currentEndTime ? "bg-slate-800 text-emerald-400" : ""
                        }`}
                        onClick={() => handleTimeChange("end", t.value)}
                      >
                        {t.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          </div>

          {/* Lead field / search (lead appointments only) */}
          {eventType === "appointment" && (
            <div className="mb-1 flex flex-col gap-1 text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500 uppercase tracking-[0.14em]">
                  Lead/Client
                </span>
                {isLeadLocked && value.lead_id && (
                  <span className="text-xs text-slate-300 truncate max-w-[260px]">
                    Linked to Lead/Client {value.lead_label || value.lead_id}
                  </span>
                )}
              </div>
              {!isLeadLocked && (
                <div className="relative">
                  <input
                    type="text"
                    value={leadQuery}
                    onChange={(e) => setLeadQuery(e.target.value)}
                    placeholder={
                      value.lead_label || value.lead_id
                        ? value.lead_label || value.lead_id
                        : "Search leads by name, phone, or address"
                    }
                    className="w-full bg-transparent border-b border-slate-700 focus:outline-none focus:border-emerald-500 text-[13px] py-1"
                  />
                  {leadResults.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto rounded-md border border-slate-700 bg-slate-950 text-xs shadow-lg">
                      {leadResults.map((lead) => {
                        const label = `${lead.name || ""}${lead.phone ? " — " + lead.phone : ""}`;
                        return (
                          <button
                            type="button"
                            key={lead.id}
                            onClick={() => {
                              onChange?.({
                                ...value,
                                lead_id: lead.id,
                                lead_label: label,
                              });
                              setLeadQuery("");
                              setLeadResults([]);
                            }}
                            className="block w-full text-left px-3 py-1.5 hover:bg-slate-800"
                          >
                            <div className="font-medium text-slate-100 truncate">{label}</div>
                            {(lead.address || lead.email) && (
                              <div className="text-xs text-slate-400 truncate">
                                {lead.address || lead.email}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end items-center gap-3 px-5 pb-3">
          <button
            type="button"
            onClick={() => onMoreOptions?.()}
            className="text-[13px] text-emerald-400 hover:underline more-options-btn"
          >
            More options
          </button>
          <button
            type="button"
            onClick={() => onSave?.()}
            className="h-9 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-sm font-medium text-slate-950 save-btn"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};


const CalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  });
  const [view, setView] = useState("day"); // day | agenda | week
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [estimatorFilter, setEstimatorFilter] = useState("all");
  const [teamMembers, setTeamMembers] = useState([]);
  const [inRouteMinutes, setInRouteMinutes] = useState(60);
  const [leadFilter, setLeadFilter] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [quickDraft, setQuickDraft] = useState(null); // { anchor: {x,y}, start: Date, end: Date }
  const [quickOpen, setQuickOpen] = useState(false);
  const [createPayload, setCreatePayload] = useState({
    event_type: "event", // "event" | "appointment"
    title: "",
    lead_id: "",
    lead_label: "",
    assigned_estimator_user_id: "",
    scheduled_start: "",
    scheduled_end: "",
    location_address: "",
    notes_internal: "",
    guests: [],
    reminder_minutes: null,
  });
  const [lastRange, setLastRange] = useState({ start: null, end: null });
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [modalLeadQuery, setModalLeadQuery] = useState("");
  useEffect(() => {
    if (!createOpen || createPayload.event_type !== "appointment" || leadFilter) {
      setModalLeadResults([]);
      return;
    }
    if (!modalLeadQuery || modalLeadQuery.length < 2) {
      setModalLeadResults([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const res = await API.get("/leads/search", {
          params: { q: modalLeadQuery, limit: 10 },
        });
        if (!cancelled) {
          setModalLeadResults(Array.isArray(res.data) ? res.data : []);
        }
      } catch (_e) {
        if (!cancelled) setModalLeadResults([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [createOpen, createPayload.event_type, leadFilter, modalLeadQuery]);

  const [modalLeadResults, setModalLeadResults] = useState([]);

  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [notesOpen, setNotesOpen] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null);
  const [notesEstimator, setNotesEstimator] = useState("");
  const [notesInternal, setNotesInternal] = useState("");

  const isOffice = roleIsOffice(user?.role);
  const isEstimator = user?.role === "estimator" || user?.role === "field_estimator";

  // derive lead filter from URL on mount/changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lid = params.get("lead_id");
    const leadLabel = params.get("lead_label");
    const shouldCreate = params.get("create") === "1";
    
    if (lid) {
      setLeadFilter(lid);
      
      // If create=1 is passed, auto-open the create form with lead pre-filled
      if (shouldCreate) {
        const now = new Date();
        const startHour = now.getHours() + 1;
        const startTime = new Date(now);
        startTime.setHours(startHour, 0, 0, 0);
        const endTime = new Date(startTime);
        endTime.setHours(startHour + 1, 0, 0, 0);
        
        setCreatePayload(prev => ({
          ...prev,
          event_type: "appointment",
          lead_id: lid,
          lead_label: leadLabel ? decodeURIComponent(leadLabel) : lid,
          scheduled_start: startTime.toISOString(),
          scheduled_end: endTime.toISOString(),
        }));
        setCreateOpen(true);
        
        // Clear the URL params after processing
        const newParams = new URLSearchParams(location.search);
        newParams.delete("create");
        newParams.delete("lead_label");
        window.history.replaceState({}, '', `${location.pathname}?${newParams.toString()}`);
      }
    }
  }, [location.search]);

  useEffect(() => {
    const fetchSettingsAndTeam = async () => {
      try {
        const [settingsRes, teamRes] = await Promise.all([
          API.get("/settings"),
          API.get("/team"),
        ]);
        const s = settingsRes.data || {};
        const value = s.in_route_required_lead_time_minutes;
        setInRouteMinutes(
          typeof value === "number" && value > 0 ? value : 60
        );
        setTeamMembers(teamRes.data || []);
      } catch (error) {
        // non-fatal
      }
    };
    fetchSettingsAndTeam();
  }, []);

  const fetchAppointments = async (startArg, endArg) => {
    const start = startArg || lastRange.start;
    const end = endArg || lastRange.end;
    if (!start || !end) return;
    setLoading(true);
    try {
      const res = await API.get("/appointments", {
        params: { start, end },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      if (process.env.NODE_ENV !== "production") {
        const unassigned = data.filter((a) => !a.assigned_estimator_user_id).length;
        const total = data.length;
        // Debug visibility for "ghost" events
        console.log("[Calendar] Loaded appointments", { total, unassigned });
      }
      setAppointments(data);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const { start: dayStart, end: dayEnd } = useMemo(
    () => getDayRange(selectedDate),
    [selectedDate]
  );
  const weekRange = useMemo(
    () => getWeekRange(selectedDate),
    [selectedDate]
  );

  const baseFiltered = useMemo(() => {
    // IMPORTANT: Do NOT apply additional date-window filtering here.
    // FullCalendar already requests appointments for the visible range
    // via datesSet → fetchAppointments(start, end). If we filter by
    // a local day/week window again, newly created events can be
    // "invisible" on the month grid.
    return appointments.filter((appt) => {
      if (!appt.scheduled_start) return false;

      if (leadFilter && appt.lead_id !== leadFilter) return false;
      if (statusFilter !== "all" && appt.status !== statusFilter) return false;
      if (estimatorFilter !== "all") {
        if (!appt.assigned_estimator_user_id) return false;
        if (appt.assigned_estimator_user_id !== estimatorFilter) return false;
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const text = [
          appt.title,
          appt.lead_label,
          appt.lead_contact_name,
          appt.lead_phone,
          appt.location_address,
          appt.assigned_estimator_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!text.includes(term)) return false;
      }

      return true;
    });
  }, [
    appointments,
    leadFilter,
    statusFilter,
    estimatorFilter,
    searchTerm,
  ]);

  const todayAppointments = useMemo(
    () =>
      baseFiltered
        .filter((a) => {
          const d = new Date(a.scheduled_start).toISOString();
          return d >= dayStart && d <= dayEnd;
        })
        .sort(
          (a, b) =>
            new Date(a.scheduled_start || 0).getTime() -
            new Date(b.scheduled_start || 0).getTime()
        ),
    [baseFiltered, dayStart, dayEnd]
  );

  const weekByDay = useMemo(() => {
    if (view !== "week") return {};
    const buckets = {};
    const monday = weekRange.monday;
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = [];
    }
    baseFiltered.forEach((appt) => {
      const key = new Date(appt.scheduled_start).toISOString().slice(0, 10);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(appt);
    });
    Object.keys(buckets).forEach((k) => {
      buckets[k].sort(
        (a, b) =>
          new Date(a.scheduled_start || 0).getTime() -
          new Date(b.scheduled_start || 0).getTime()
      );
    });

    return buckets;
  }, [baseFiltered, weekRange, view]);

  const handleDatesSet = (arg) => {
    const startIso = arg.start.toISOString();
    const endIso = arg.end.toISOString();
    setLastRange({ start: startIso, end: endIso });
    fetchAppointments(startIso, endIso);
  };

  // Person-specific selector (any active team member)
  const activeTeamOptions = teamMembers.filter((m) => m?.status !== "inactive" && m?.status !== "archived");

  const handleDateChange = (deltaDays) => {
    const current = new Date(selectedDate || new Date());
    current.setDate(current.getDate() + deltaDays);
    setSelectedDate(current.toISOString().slice(0, 10));
  };

  const handleStatusChange = async (appt, newStatus) => {
    try {
      await API.post(`/appointments/${appt.appointment_id}/status`, {
        status: newStatus,
      });
      toast.success("Status updated");
      fetchAppointments();
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to update status";
      toast.error(msg);
    }
  };

  const clearLeadFilter = () => {
    setLeadFilter(null);
    const params = new URLSearchParams(location.search);
    params.delete("lead_id");
    navigate({ pathname: "/appointments", search: params.toString() });
  };

  const nextAppointment = useMemo(() => {
    if (!isEstimator) return null;
    const now = Date.now();
    const own = todayAppointments.filter(
      (a) =>
        a.assigned_estimator_user_id === user?.user_id &&
        ACTIVE_STATUSES.includes(a.status)
    );
    if (own.length === 0) return null;
    return own.reduce((closest, appt) => {
      const t = appt.scheduled_start
        ? new Date(appt.scheduled_start).getTime()
        : now + 999999999;
      if (t < now) return closest;
      if (!closest) return appt;
      const ct = closest.scheduled_start
        ? new Date(closest.scheduled_start).getTime()
        : now + 999999999;
      return t < ct ? appt : closest;
    }, null);
  }, [todayAppointments, isEstimator, user]);

  const openLead = (leadId) => {
    if (!leadId) return;
    navigate(`/leads?lead_id=${encodeURIComponent(leadId)}`);
  };

  const openNotes = (appt) => {
    setNotesTarget(appt);
    setNotesEstimator(appt.notes_estimator || "");
    setNotesInternal(appt.notes_internal || "");
    setNotesOpen(true);
  };

  const saveNotes = async () => {
    if (!notesTarget) return;
    try {
      if (isEstimator && notesEstimator !== notesTarget.notes_estimator) {
        await API.post(
          `/appointments/${notesTarget.appointment_id}/estimator-notes`,
          { notes_estimator: notesEstimator }
        );
      }
      if (roleIsOffice(user?.role) && notesInternal !== notesTarget.notes_internal) {
        await API.post(
          `/appointments/${notesTarget.appointment_id}/internal-notes`,
          { notes_internal: notesInternal }
        );
      }
      toast.success("Notes updated");
      setNotesOpen(false);
      setNotesTarget(null);
      fetchAppointments();
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to save notes";
      toast.error(msg);
    }
  };

  const handleCreateAppointment = async () => {
    const isEditMode = !!editingAppointment?.appointment_id;
    if (!createPayload.scheduled_start) {
      toast.error("Start time is required");
      return;
    }

    const eventType = createPayload.event_type || (leadFilter ? "appointment" : "event");
    const trimmedTitle = (createPayload.title || "").trim();

    if (eventType === "event" && !trimmedTitle) {
      toast.error("Title is required for events");
      return;
    }

    if (eventType === "appointment" && !createPayload.lead_id && !trimmedTitle) {
      toast.error("Provide a title or select a lead");
      return;
    }

    setCreateSubmitting(true);
    try {
      // Interpret stored values as local datetime and convert at POST time
      let scheduled_end_local = createPayload.scheduled_end;
      const startLocal = createPayload.scheduled_start;
      let startIso = undefined;
      let endIso = undefined;

      if (startLocal) {
        const start = new Date(startLocal);
        startIso = start.toISOString();
      }

      if (!scheduled_end_local && startLocal) {
        const start = new Date(startLocal);
        const end = new Date(start.getTime() + 60 * 60000);
        endIso = end.toISOString();
      } else if (scheduled_end_local) {
        const end = new Date(scheduled_end_local);
        endIso = end.toISOString();
      }

      const body = {
        event_type: eventType,
        title: trimmedTitle || undefined,
        lead_id: createPayload.lead_id || undefined,
        assigned_estimator_user_id:
          createPayload.assigned_estimator_user_id === "unassigned"
            ? undefined
            : createPayload.assigned_estimator_user_id || undefined,
        scheduled_start: startIso,
        scheduled_end: endIso,
        location_address: createPayload.location_address || undefined,
        notes_internal: createPayload.notes_internal || undefined,
        guests: createPayload.guests && createPayload.guests.length ? createPayload.guests : undefined,
        reminder_minutes:
          typeof createPayload.reminder_minutes === "number"
            ? createPayload.reminder_minutes
            : undefined,
      };

      if (isEditMode) {
        // Check if time has changed - compare as timestamps to handle timezone differences
        const originalStartTime = new Date(editingAppointment.scheduled_start).getTime();
        const originalEndTime = new Date(editingAppointment.scheduled_end).getTime();
        const newStartTime = startIso ? new Date(startIso).getTime() : 0;
        const newEndTime = endIso ? new Date(endIso).getTime() : 0;
        const timeChanged = newStartTime !== originalStartTime || newEndTime !== originalEndTime;
        
        if (timeChanged) {
          // Use reschedule endpoint for time changes
          await API.post(`/appointments/${editingAppointment.appointment_id}/reschedule`, {
            scheduled_start: startIso,
            scheduled_end: endIso,
            reason: "Time updated via calendar"
          });
        }
        
        // Update other fields via PUT (excluding time fields)
        const updateBody = {
          assigned_estimator_user_id: body.assigned_estimator_user_id,
          location_address: body.location_address,
          notes_internal: body.notes_internal,
        };
        
        // Only call PUT if there are non-time fields to update
        const hasUpdates = Object.values(updateBody).some(v => v !== undefined);
        if (hasUpdates) {
          await API.put(`/appointments/${editingAppointment.appointment_id}`, updateBody);
        }
        
        toast.success("Appointment updated");
      } else {
        await API.post("/appointments", body);
        toast.success("Appointment created");
      }
      setCreateOpen(false);
      setEditingAppointment(null);
      setCreatePayload({
        event_type: "event",
        title: "",
        lead_id: "",
        lead_label: "",
        assigned_estimator_user_id: "",
        scheduled_start: "",
        scheduled_end: "",
        location_address: "",
        notes_internal: "",
        guests: [],
        reminder_minutes: null,
      });
      fetchAppointments();
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to create appointment";
      toast.error(msg);
    } finally {
      setCreateSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <CalendarIcon className="h-6 w-6 text-emerald-400" />
            Appointments
          </h1>
          <p className="text-sm text-slate-400">
            {isOffice
              ? "Office control for estimator routes and time windows."
              : "Your field schedule and status controls."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {leadFilter && (
            <Button
              variant="outline"
              className="h-8 px-3 text-xs border-amber-500 text-amber-300 hover:bg-amber-900/40"
              onClick={clearLeadFilter}
            >
              Clear lead filter
            </Button>
          )}
          {isOffice && (
            <Button
              className="h-9 px-4 bg-emerald-600 hover:bg-emerald-500 text-xs rounded-full"
              onClick={() => {
                setEditingAppointment(null);
                setCreatePayload((prev) => ({
                  ...prev,
                  event_type: leadFilter ? "appointment" : "event",
                }));
                setCreateOpen(true);
              }}
            >
              Create Appointment
            </Button>
          )}
          <Button
            variant="outline"
            className="h-9 px-3 text-xs border-slate-600 text-slate-200 hover:bg-slate-800 rounded-md"
            onClick={() => setSelectedDate(new Date().toISOString().slice(0, 10))}
          >
            Today
          </Button>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 border-slate-600 text-slate-200 hover:bg-slate-800 rounded-md"
              onClick={() => handleDateChange(-1)}
            >
              &lt;
            </Button>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-9 w-[150px] bg-slate-900 border-slate-700 text-slate-100"
            />
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 border-slate-600 text-slate-200 hover:bg-slate-800 rounded-md"
              onClick={() => handleDateChange(1)}
            >
              &gt;
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 flex items-center gap-2">
          <Input
            placeholder="Search by name, phone, address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 bg-slate-900 border-slate-700 text-slate-100"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[140px] bg-slate-900 border-slate-700 text-slate-100">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.keys(statusColors).map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isOffice && (
            <Select value={estimatorFilter} onValueChange={setEstimatorFilter}>
              <SelectTrigger className="h-9 w-[200px] bg-slate-900 border-slate-700 text-slate-100">
                <SelectValue placeholder="All Team Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Members</SelectItem>
                {activeTeamOptions.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="border border-slate-800 rounded-lg bg-slate-950">
        <style>{`
          /* Google-like text-only events for dayGrid (no pill background) */
          .fc .fc-daygrid-event {
            background: transparent !important;
            border: none !important;
            padding: 0 2px;
            margin-top: 1px;
            margin-bottom: 1px;
            font-size: 11px;
            line-height: 1.2;
            display: block;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
          .fc .fc-daygrid-event .fc-event-title {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          /* TimeGrid events */
          .fc .fc-timegrid-event {
            border-radius: 6px !important;
            padding: 4px 6px !important;
            font-size: 12px;
          }
          .fc .fc-timegrid-event .fc-event-title {
            font-weight: 600;
          }
          /* Status-based classes */
          .appt--scheduled { background-color: rgb(51 65 85); color: rgb(248 250 252); }
          .appt--confirmed { background-color: rgb(16 185 129); color: rgb(15 23 42); }
          .appt--in_route { background-color: rgb(245 158 11); color: rgb(15 23 42); }
          .appt--arrived { background-color: rgb(5 150 105); color: white; }
          .appt--completed { background-color: rgb(4 120 87); color: white; }
          .appt--no_show { background-color: rgb(185 28 28); color: white; }
          .appt--canceled { background-color: rgb(71 85 105); color: rgb(248 250 252); }
          .appt--unassigned { border: 1px dashed rgba(148, 163, 184, 0.7); }
          /* ================================
             QUICK CREATE / MODAL CARD FIX
             ================================ */

          /* Main card container */
          .calendar-quick-card,
          .fc .calendar-quick-card,
          .fc .fc-popover,
          .fc .fc-dialog {
            background: #020617 !important; /* slate-950 */
            border: 1px solid rgb(30 41 59) !important; /* slate-800 */
            border-radius: 12px;
            color: rgb(241 245 249); /* slate-100 */
            box-shadow: 0 20px 50px rgba(0,0,0,0.6);
          }

          /* Header strip */
          .calendar-quick-card-header {
            background: rgb(15 23 42); /* slate-900 */
            border-bottom: 1px solid rgb(30 41 59);
          }

          /* Title input */
          .calendar-quick-card input[type="text"],
          .calendar-quick-card input[type="email"],
          .calendar-quick-card textarea {
            background: transparent !important;
            color: rgb(241 245 249);
            border-bottom: 1px solid rgb(51 65 85); /* slate-700 */
          }

          .calendar-quick-card input::placeholder,
          .calendar-quick-card textarea::placeholder {
            color: rgb(148 163 184); /* slate-400 */
          }

          /* Date/time pickers */
          .calendar-quick-card .date-input,
          .calendar-quick-card .time-input {
            background: rgb(15 23 42); /* slate-900 */
            border: 1px solid rgb(51 65 85);
            color: rgb(241 245 249);
          }

          /* Action buttons */
          .calendar-quick-card .save-btn {
            background: rgb(16 185 129); /* emerald-500 */
            color: #022c22;
          }

          .calendar-quick-card .more-options-btn {
            color: rgb(110 231 183); /* emerald-300 */
          }

          /* Weekday header row background */
          .fc .fc-col-header-cell {
            background: rgb(30 41 59) !important; /* slate-800 */
          }

          /* Weekday header text */
          .fc .fc-col-header-cell-cushion {
            color: rgb(241 245 249) !important; /* slate-100 */
            font-weight: 700 !important;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            padding: 6px 8px;
            display: block;
          }

          /* Month grid height */
          .fc .fc-daygrid-day-frame {
            min-height: 120px !important;
          }

          /* Give events a little breathing room */
          .fc .fc-daygrid-day-events {
            margin-top: 6px;
          }

          /* Date number top-right */
          .fc .fc-dayGridMonth-view .fc-daygrid-day-top {
            display: flex;
            justify-content: flex-end;
          }
          .fc .fc-dayGridMonth-view .fc-daygrid-day-number {
            font-size: 1.5rem; /* ~24px for strong visibility */
            font-weight: 700;
            line-height: 1;
            padding: 8px;
            color: rgb(241, 245, 249); /* slate-100 */
          }

          /* Subtle borders */
          .fc .fc-dayGridMonth-view .fc-theme-standard td,
          .fc .fc-dayGridMonth-view .fc-theme-standard th {
            border-color: rgba(148, 163, 184, 0.15);
          }

          /* Today highlight */
          .fc .fc-dayGridMonth-view .fc-day-today {
            background: rgba(16, 185, 129, 0.08);
          }

          /* "+N more" readability */
          .fc .fc-dayGridMonth-view .fc-daygrid-more-link {
            font-size: 11px;
            font-weight: 600;
            color: rgb(94, 234, 212);
          }
          
          /* FullCalendar button styles */
          .fc .fc-button-primary {
            background: rgba(15, 23, 42, 0.95);
            border: 1px solid rgba(75, 85, 99, 0.9);
            color: rgb(226, 232, 240);
            font-size: 12px;
            border-radius: 0.375rem;
            padding: 4px 10px;
          }
          .fc .fc-button-primary:hover {
            background: rgba(15, 23, 42, 1);
            border-color: rgba(45, 212, 191, 0.9);
            color: rgb(45, 212, 191);
          }
        `}</style>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          }}
          height="auto"
          firstDay={1}
          selectable={isOffice}
          selectMirror={true}
          dayMaxEvents={2}
          nowIndicator={true}
          editable={false}
          eventStartEditable={false}
          eventDurationEditable={false}
          events={baseFiltered.map((appt) => {
            const start = appt.scheduled_start;
            const end =
              appt.scheduled_end ||
              (start
                ? new Date(new Date(start).getTime() + 60 * 60000).toISOString()
                : undefined);
            const label =
              appt.title ||
              appt.lead_label ||
              appt.lead_contact_name ||
              (appt.event_type === "event" ? "Event" : "Lead");
            return {
              id: appt.appointment_id,
              title: label,
              start,
              end,
              extendedProps: { appt },
            };
          })}
          eventContent={(arg) => {
            const appt = arg.event.extendedProps.appt;

            const baseLabel =
              appt?.title ||
              appt?.lead_label ||
              appt?.lead_contact_name ||
              (appt?.event_type === "event" ? "Event" : "Lead");
            const timePrefix = appt?.scheduled_start
              ? `${formatTime(appt.scheduled_start)} `
              : "";
            const label = `${timePrefix}${baseLabel}`;

            return <span className="fc-event-title block truncate">{label}</span>;
          }}
          datesSet={handleDatesSet}
          eventClick={(info) => {
            const appt = info.event.extendedProps.appt;
            if (!appt) return;
            if (!isOffice) {
              setNotesTarget(appt);
              setNotesEstimator(appt.notes_estimator || "");
              setNotesInternal(appt.notes_internal || "");
              setNotesOpen(true);
              return;
            }
            setEditingAppointment(appt);
            // Convert ISO dates to local datetime format for datetime-local inputs
            const startLocal = appt.scheduled_start 
              ? formatLocalDateTime(new Date(appt.scheduled_start)) 
              : "";
            const endLocal = appt.scheduled_end 
              ? formatLocalDateTime(new Date(appt.scheduled_end)) 
              : "";
            setCreatePayload({
              event_type: appt.event_type || "event",
              title: appt.title || "",
              lead_id: appt.lead_id || "",
              lead_label: appt.lead_label || "",
              assigned_estimator_user_id: appt.assigned_estimator_user_id || "",
              scheduled_start: startLocal,
              scheduled_end: endLocal,
              location_address: appt.location_address || "",
              notes_internal: appt.notes_internal || "",
              guests: Array.isArray(appt.guests) ? appt.guests : [],
              reminder_minutes:
                typeof appt.reminder_minutes === "number" ? appt.reminder_minutes : null,
            });
            setCreateOpen(true);
          }}
          dateClick={(info) => {
            if (!isOffice) return;
            const dateStr = info.dateStr; // "YYYY-MM-DD" from FullCalendar
            // Default time block 9–10 AM local on the clicked day
            const startLocal = `${dateStr}T09:00`;
            const endLocal = `${dateStr}T10:00`;

            setCreatePayload((prev) => ({
              ...prev,
              event_type: leadFilter ? "appointment" : "event",
              lead_id: leadFilter || prev.lead_id,
              scheduled_start: startLocal,
              scheduled_end: endLocal,
            }));

            // Open FULL Google-style modal directly (no quick card)
            setCreateOpen(true);
          }}
          select={(info) => {
            if (!isOffice) return;
            const start = info.start;
            const end = info.end;
            const startLocal = formatLocalDateTime(start);
            const endLocal = formatLocalDateTime(end);

            setCreatePayload((prev) => ({
              ...prev,
              event_type: leadFilter ? "appointment" : "event",
              lead_id: leadFilter || prev.lead_id,
              scheduled_start: startLocal,
              scheduled_end: endLocal,
            }));

            // For drag-select ranges, also open the full modal directly
            setCreateOpen(true);
          }}
          eventClassNames={(arg) => {
            const appt = arg.event.extendedProps.appt;
            const status = appt?.status;
            const classes = ["border-none text-xs leading-tight cursor-pointer"];
            if (status) {
              classes.push(`appt--${status}`);
            }
            if (!appt?.assigned_estimator_user_id) {
              classes.push("appt--unassigned");
            }
            return classes;
          }}
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: true }}
        />
      </div>

      {/* Google Calendar-style Quick Create Card */}
      {isOffice && quickOpen && quickDraft && (
        <GCalQuickCreateCard
          open={quickOpen}
          anchor={quickDraft.anchor}
          value={{
            title: createPayload.title || "",
            lead_id: leadFilter || createPayload.lead_id,
            lead_label: createPayload.lead_label,
            startLocal: createPayload.scheduled_start,
            endLocal: createPayload.scheduled_end,
          }}
          isLeadLocked={!!leadFilter}
          eventType={createPayload.event_type || (leadFilter ? "appointment" : "event")}
          onEventTypeChange={(type) =>
            setCreatePayload((prev) => ({
              ...prev,
              event_type: type,
            }))
          }
          onChange={(val) => {
            setCreatePayload((prev) => ({
              ...prev,
              title: val.title,
              lead_id: val.lead_id || prev.lead_id,
              lead_label: val.lead_label || prev.lead_label,
              notes_internal: val.title,
              scheduled_start: val.startLocal,
              scheduled_end: val.endLocal,
            }));
          }}
          onSave={() => {
            handleCreateAppointment();
            setQuickOpen(false);
            setQuickDraft(null);
          }}
          onMoreOptions={() => {
            setQuickOpen(false);
            setQuickDraft(null);
            setCreateOpen(true);
          }}
          onClose={() => {
            setQuickOpen(false);
            setQuickDraft(null);
          }}
        />
      )}

      {/* Notes Modal */}
      {notesOpen && notesTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 w-full max-w-md">
            <h2 className="text-sm font-semibold text-slate-100 mb-2">
              Appointment Notes
            </h2>
            <p className="text-xs text-slate-400 mb-3">
              {notesTarget?.lead_contact_name || "Lead"} · {formatTime(notesTarget?.scheduled_start)}
            </p>
            <div className="space-y-3">
              {isEstimator && (
                <div>
                  <label className="block text-xs text-slate-300 mb-1">
                    Estimator Notes
                  </label>
                  <textarea
                    className="w-full min-h-[80px] bg-slate-950 border border-slate-700 rounded-md text-xs text-slate-100 p-2"
                    value={notesEstimator}
                    onChange={(e) => setNotesEstimator(e.target.value)}
                  />
                </div>
              )}
              {isOffice && (
                <div>
                  <label className="block text-xs text-slate-300 mb-1">
                    Internal Notes
                  </label>
                  <textarea
                    className="w-full min-h-[80px] bg-slate-950 border border-slate-700 rounded-md text-xs text-slate-100 p-2"
                    value={notesInternal}
                    onChange={(e) => setNotesInternal(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3 border-slate-600 text-xs"
                onClick={() => {
                  setNotesOpen(false);
                  setNotesTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs"
                onClick={saveNotes}
              >
                Save Notes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event / Appointment Modal (full editor, Google Calendar style) */}
      {isOffice && createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-[360px] h-[550px] max-w-full bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black font-sans text-slate-100 relative overflow-hidden">
            {/* Header strip */}
            <div className="bg-slate-900 h-10 flex justify-between items-center px-4 border-b border-slate-800">
              <span className="text-xs uppercase tracking-[0.14em] text-slate-500">
                Appointment details
              </span>
              <button
                className="text-slate-400 hover:bg-slate-800 rounded-full p-1"
                onClick={() => {
                  setCreateOpen(false);
                  setEditingAppointment(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="p-4 pt-3 h-[510px] overflow-y-auto space-y-4">
              {/* Title */}
              <input
                type="text"
                placeholder="Add title"
                value={createPayload.title || ""}
                onChange={(e) =>
                  setCreatePayload((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                className="w-full text-[22px] border-b border-slate-700 focus:border-emerald-500 outline-none py-2 mb-4 placeholder-slate-500 text-slate-100 bg-transparent"
              />

              {/* Type chips */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() =>
                    setCreatePayload((prev) => ({
                      ...prev,
                      event_type: "event",
                    }))
                  }
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    createPayload.event_type === "event"
                      ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                      : "text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  Event
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCreatePayload((prev) => ({
                      ...prev,
                      event_type: "appointment",
                    }))
                  }
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    createPayload.event_type === "appointment"
                      ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                      : "text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  Appointment
                </button>
              </div>

              {/* Time row */}
              <div className="flex items-start gap-3 mb-3">
                <Clock className="w-4 h-4 text-slate-400 mt-1" />
                <div className="flex flex-col w-full text-sm gap-2">
                  <input
                    type="datetime-local"
                    value={createPayload.scheduled_start}
                    onChange={(e) =>
                      setCreatePayload((prev) => ({
                        ...prev,
                        scheduled_start: e.target.value,
                      }))
                    }
                    className="w-full bg-transparent border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 focus:outline-none focus:border-emerald-500"
                  />
                  <input
                    type="datetime-local"
                    value={createPayload.scheduled_end}
                    onChange={(e) =>
                      setCreatePayload((prev) => ({
                        ...prev,
                        scheduled_end: e.target.value,
                      }))
                    }
                    className="w-full bg-transparent border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Lead/Client row (appointments only) */}
              {createPayload.event_type === "appointment" && (
                <div className="mb-2 text-sm">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-slate-400 uppercase tracking-[0.14em]">
                        Lead/Client
                      </span>
                    </div>
                    {(leadFilter || createPayload.lead_label || createPayload.lead_id) && (
                      <span className="text-xs text-slate-300 truncate max-w-[180px]">
                        {createPayload.lead_label || createPayload.lead_id || leadFilter}
                      </span>
                    )}
                  </div>
                  {!leadFilter && (
                    <div className="relative">
                      <input
                        type="text"
                        value={modalLeadQuery}
                        onChange={(e) => setModalLeadQuery(e.target.value)}
                        placeholder={
                          createPayload.lead_label || createPayload.lead_id
                            ? createPayload.lead_label || createPayload.lead_id
                            : "Search leads by name, phone, or address"
                        }
                        className="w-full bg-transparent border-b border-slate-700 focus:outline-none focus:border-emerald-500 text-sm py-1 placeholder:text-slate-500"
                        style={{ caretColor: "#22c55e" }}
                      />
                      {modalLeadResults.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto rounded-md border border-slate-700 bg-slate-950 text-xs shadow-lg">
                          {modalLeadResults.map((lead) => {
                            const label = `${lead.name || ""}${lead.phone ? " — " + lead.phone : ""}`;
                            return (
                              <button
                                type="button"
                                key={lead.id}
                                onClick={() => {
                                  setCreatePayload((prev) => ({
                                    ...prev,
                                    lead_id: lead.id,
                                    lead_label: label,
                                  }));
                                  setModalLeadQuery("");
                                  setModalLeadResults([]);
                                }}
                                className="block w-full text-left px-3 py-1.5 hover:bg-slate-800"
                              >
                                <div className="font-medium text-slate-100 truncate">{label}</div>
                                {(lead.address || lead.email) && (
                                  <div className="text-xs text-slate-400 truncate">
                                    {lead.address || lead.email}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Guests row */}
              <div className="flex items-center gap-3 mb-2 group">
                <User className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                <input
                  type="text"
                  placeholder="Add guests (comma-separated emails)"
                  value={createPayload.guests?.join(", ") || ""}
                  onChange={(e) =>
                    setCreatePayload((prev) => ({
                      ...prev,
                      guests: e.target.value
                        .split(",")
                        .map((g) => g.trim())
                        .filter(Boolean),
                    }))
                  }
                  className="w-full outline-none text-sm placeholder-slate-500 py-1 bg-transparent border-b border-slate-700 focus:border-emerald-500"
                />
              </div>

              {/* Location row */}
              <div className="flex items-center gap-3 mb-2 group">
                <MapPin className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                <div className="flex flex-col w-full text-sm">
                  <input
                    type="text"
                    placeholder="Add location"
                    value={createPayload.location_address || ""}
                    onChange={(e) =>
                      setCreatePayload((prev) => ({
                        ...prev,
                        location_address: e.target.value,
                      }))
                    }
                    className="w-full outline-none text-sm placeholder-slate-500 py-1 bg-transparent border-b border-slate-700 focus:border-emerald-500"
                  />
                  {createPayload.location_address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        createPayload.location_address,
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 text-xs text-emerald-400 hover:underline"
                    >
                      View on map
                    </a>
                  )}
                </div>
              </div>

              {/* Description row */}
              <div className="flex items-start gap-3 mb-3 group">
                <AlertTriangle className="w-4 h-4 text-gray-400 group-hover:text-gray-600 mt-1" />
                <textarea
                  placeholder="Add description"
                  rows={1}
                  value={createPayload.notes_internal || ""}
                  onChange={(e) =>
                    setCreatePayload((prev) => ({
                      ...prev,
                      notes_internal: e.target.value,
                    }))
                  }
                  className="w-full outline-none text-sm placeholder-slate-500 py-1 bg-transparent border-b border-slate-700 focus:border-emerald-500 resize-none"
                />
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center gap-2 pt-2">
                {editingAppointment && (
                  <button
                    type="button"
                    className="text-xs text-red-400 hover:text-red-300 hover:bg-red-950/40 px-3 py-2 rounded"
                    onClick={async () => {
                      if (!editingAppointment) return;
                      const confirmed = window.confirm(
                        "This will permanently remove the appointment from the calendar. Continue?",
                      );
                      if (!confirmed) return;
                      try {
                        await API.delete(`/appointments/${editingAppointment.appointment_id}`);
                        toast.success("Appointment deleted");
                        setCreateOpen(false);
                        setEditingAppointment(null);
                        fetchAppointments();
                      } catch (error) {
                        const msg = error?.response?.data?.detail || "Failed to delete appointment";
                        toast.error(msg);
                      }
                    }}
                  >
                    Delete
                  </button>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    type="button"
                    className="text-emerald-400 text-sm font-medium hover:bg-emerald-500/10 px-3 py-2 rounded"
                    onClick={() => {
                      setCreateOpen(false);
                      setEditingAppointment(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded text-sm font-medium shadow-sm"
                    onClick={handleCreateAppointment}
                  >
                    {editingAppointment ? "Save changes" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
